import type {
  GetLocations,
  GetResources,
  GetServices,
  GetUsers,
} from "@/server-types";
import type { CalendarFilterOption } from "./types";

type LocationOptionSource = Pick<
  GetLocations["response"]["locations"][number],
  "locationId" | "label" | "permission"
>;
type UserOptionSource = Pick<
  GetUsers["response"]["users"][number],
  "userId" | "name" | "lastName"
>;
type ServiceOptionSource = Pick<
  GetServices["response"]["services"][number],
  "serviceId" | "name" | "permission"
>;
type ResourceOptionSource = Pick<
  GetResources["response"][number],
  "id" | "label" | "isEnabled"
>;

// The list itself is guarded, not just the outer object: a non-conforming 200
// body (auth/gateway HTML, a garbage cached response) can make the payload
// truthy while its array is absent, which previously crashed the calendar
// filter drawer (DEV-10585).
export function toLocationFilterOptions(
  locationsData: { locations?: LocationOptionSource[] } | undefined,
): CalendarFilterOption[] {
  return (
    locationsData?.locations
      ?.filter((location) => location.permission !== false)
      .map((location) => ({
        id: location.locationId,
        label: location.label ?? `#${location.locationId}`,
      })) ?? []
  );
}

export function toUserFilterOptions(
  usersData: { users?: UserOptionSource[] } | undefined,
): CalendarFilterOption[] {
  return (
    usersData?.users?.map((user) => ({
      id: user.userId,
      label: `${user.name} ${user.lastName}`.trim(),
    })) ?? []
  );
}

export function toServiceFilterOptions(
  servicesData: { services?: ServiceOptionSource[] } | undefined,
): CalendarFilterOption[] {
  return (
    servicesData?.services
      ?.filter((service) => service.permission !== false)
      .map((service) => ({
        id: service.serviceId,
        label: service.name,
      })) ?? []
  );
}

// Unlike the others, `/resource` returns the array at the top level, so
// `Array.isArray` is what guards a truthy non-array body (DEV-10635/DEV-10585).
export function toResourceFilterOptions(
  resourcesData: ResourceOptionSource[] | undefined,
): CalendarFilterOption[] {
  if (!Array.isArray(resourcesData)) {
    return [];
  }
  return resourcesData
    .filter((resource) => resource.isEnabled)
    .map((resource) => ({
      id: resource.id,
      label: resource.label,
    }));
}
